import { AttachmentGallery } from "./AttachmentGallery";
import React from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { Clock, Pin, MessageCircle, Eye, Trash2 } from "lucide-react";
import { formatTimeAgo } from "./utils";

export const PostList = ({
  posts,
  discussionTitle,
  postsCount,
  viewsCount,
  onDeleteDiscussion,
  onTogglePinPost,
  canPin = false,
}) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", fontFamily: "'Montserrat', sans-serif" }}>
      {posts.map((post, index) => {
        const isFirstPost = index === 0;
        const isPinned = Boolean(post.is_pinned);
        
        return (
          <div
            key={post.id}
            style={{
              backgroundColor: isFirstPost
                ? (isDark ? "#1e293b" : (theme.colors.cardBackground || "#ffffff"))
                : isPinned
                ? (isDark ? "#171c2e" : "#fef9ee")
                : (isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff")),
              border: isFirstPost
                ? `2px solid #ECB236`
                : isPinned
                ? `1.5px solid rgba(236, 178, 54, 0.65)`
                : "1px solid rgba(255,255,255,0.2)",
              borderRadius: "12px",
              padding: "28px",
              boxShadow: isFirstPost
                ? "0 4px 12px rgba(236, 178, 54, 0.2)"
                : isPinned
                ? "0 4px 16px rgba(236, 178, 54, 0.12)"
                : "0 2px 8px rgba(0,0,0,0.08)",
              animation: `slideInPost 0.3s ease-out ${index * 0.05}s both`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <style>{`
              @keyframes slideInPost {
                from {
                  opacity: 0;
                  transform: translateY(-15px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>

            {/* First post: Discussion title */}
            {isFirstPost && (
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "12px", flexWrap: "wrap" }}>
                  <span style={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "var(--font-size-min, 15px)", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Clock size={16} />
                    {post.discussion_created_at ? new Date(post.discussion_created_at * 1000).toLocaleDateString() : ""}
                  </span>
                  {onDeleteDiscussion && (
                    <button
                      type="button"
                      onClick={onDeleteDiscussion}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 14px",
                        borderRadius: "8px",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                        color: "#ef4444",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "var(--font-size-min, 15px)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.1)";
                      }}
                      title="Eliminar esta discusión"
                    >
                      <Trash2 size={16} />
                      Eliminar discusión
                    </button>
                  )}
                </div>
                <h1
                  className="font-outfit"
                  style={{
                    color: theme.colors.textColor || theme.colors.textColorNavbar,
                    margin: 0,
                    fontSize: "2.4rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {discussionTitle || "Discusión"}
                </h1>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "12px" }}>
                  <span style={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "var(--font-size-min, 15px)", display: "flex", alignItems: "center", gap: "6px" }}>
                    <MessageCircle size={18} />
                    {postsCount - 1} respuestas
                  </span>
                  <span style={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: "var(--font-size-min, 15px)", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Eye size={18} />
                    {viewsCount || 0} vistas
                  </span>
                </div>
              </div>
            )}

            {/* Header: Avatar + Author info */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "16px",
                borderTop: isFirstPost ? `1px solid ${isDark ? "#334155" : "#e2e8f0"}` : "none",
                paddingTop: isFirstPost ? "20px" : "0",
              }}
            >
              <img
                src={post.author_picture || "https://via.placeholder.com/48"}
                alt={post.author_name}
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: `2px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                  flexShrink: 0,
                }}
              />
              
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                <span
                  style={{
                    fontWeight: 700,
                    color: theme.colors.textColor || theme.colors.textColorNavbar,
                    fontSize: "var(--font-size-body, 16px)",
                  }}
                >
                  {post.author_name}
                </span>
                <span
                  style={{
                    color: isDark ? "#64748b" : "#94a3b8",
                    fontSize: "var(--font-size-min, 15px)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Clock size={16} />
                  {formatTimeAgo(post.created_at)}
                </span>
              </div>

              {/* Badges and action buttons */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {isFirstPost && (
                  <span
                    style={{
                      backgroundColor: "#ECB23620",
                      color: "#ECB236",
                      padding: "4px 12px",
                      borderRadius: "8px",
                      fontSize: "var(--font-size-min, 15px)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Pin size={14} />
                    Original
                  </span>
                )}

                {!isFirstPost && isPinned && (
                  <span
                    style={{
                      backgroundColor: "rgba(236, 178, 54, 0.15)",
                      color: "#ECB236",
                      border: "1px solid rgba(236, 178, 54, 0.35)",
                      padding: "4px 12px",
                      borderRadius: "8px",
                      fontSize: "var(--font-size-min, 15px)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Pin size={13} />
                    Fijado
                  </span>
                )}

                {!isFirstPost && canPin && onTogglePinPost && (
                  <button
                    type="button"
                    onClick={() => onTogglePinPost(post.id)}
                    title={isPinned ? "Desfijar esta respuesta" : "Fijar esta respuesta"}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 14px",
                      borderRadius: "8px",
                      border: `1px solid ${isPinned ? "#ECB236" : "rgba(236, 178, 54, 0.45)"}`,
                      backgroundColor: isPinned ? "rgba(236, 178, 54, 0.18)" : "rgba(236, 178, 54, 0.08)",
                      color: "#ECB236",
                      cursor: "pointer",
                      fontSize: "var(--font-size-min, 15px)",
                      fontWeight: 600,
                      fontFamily: "'Montserrat', sans-serif",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(236, 178, 54, 0.25)";
                      e.currentTarget.style.borderColor = "#ECB236";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isPinned ? "rgba(236, 178, 54, 0.18)" : "rgba(236, 178, 54, 0.08)";
                      e.currentTarget.style.borderColor = isPinned ? "#ECB236" : "rgba(236, 178, 54, 0.45)";
                    }}
                  >
                    <Pin size={14} />
                    {isPinned ? "Desfijar" : "Fijar"}
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <p
              style={{
                color: theme.colors.textColor || theme.colors.textColorNavbar,
                lineHeight: 1.65,
                margin: 0,
                whiteSpace: "pre-wrap",
                fontSize: "var(--font-size-body, 16px)",
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 500,
              }}
            >
              {post.content}
            </p>
            <AttachmentGallery files={post.attachments || []} />
          </div>
        );
      })}
    </div>
  );
};
